import { useEffect, useRef, useState } from 'react'
import { useUIStore, THEMES, type ThemeId } from '@/stores/uiStore'

interface ThemePickerProps {
  /**
   * - `icon`: compact trigger button that opens a popover (header use).
   * - `menu`: renders the four themes inline as selectable rows —
   *   intended for embedding inside another menu (UserMenu dropdown).
   */
  variant?: 'icon' | 'menu'
  className?: string
}

/**
 * Theme switcher. Each option's swatch is painted with its own
 * theme's `--accent` value (by nesting in `.theme-<id>`), so the
 * swatch shows the destination color even when a different theme is
 * currently active.
 */
export function ThemePicker({
  variant = 'icon',
  className = '',
}: ThemePickerProps) {
  if (variant === 'menu') return <ThemeMenu className={className} />
  return <ThemeIconPopover className={className} />
}

function ThemeMenu({ className = '' }: { className?: string }) {
  const theme = useUIStore(s => s.theme)
  const setTheme = useUIStore(s => s.setTheme)

  return (
    <div className={className} role="group" aria-label="Theme">
      <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-content-tertiary">
        Theme
      </div>
      {THEMES.map(t => {
        const active = t.id === theme
        return (
          <button
            key={t.id}
            type="button"
            aria-pressed={active}
            onClick={() => setTheme(t.id)}
            className={`w-full flex items-center gap-3 px-3 py-1.5 text-left text-sm transition-colors duration-150 ${
              active
                ? 'text-content-primary bg-accent-muted'
                : 'text-content-secondary hover:text-content-primary hover:bg-accent-muted'
            }`}
          >
            <ThemeSwatch themeId={t.id} size={10} />
            <span className="flex-1">{t.label}</span>
            {active && (
              <span
                aria-hidden="true"
                className="text-xs text-content-tertiary"
              >
                ✓
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

function ThemeIconPopover({ className = '' }: { className?: string }) {
  const theme = useUIStore(s => s.theme)
  const setTheme = useUIStore(s => s.setTheme)
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open])

  const activeLabel = THEMES.find(t => t.id === theme)?.label ?? 'Theme'

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Theme: ${activeLabel}`}
        title={activeLabel}
        className="inline-flex items-center justify-center h-9 w-9 rounded-lg bg-surface-sunken hover:bg-surface-overlay border border-brd-subtle hover:border-brd-emphasis transition-colors duration-150"
      >
        <ThemeSwatch themeId={theme} size={14} />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute right-0 mt-2 min-w-[200px] rounded-xl border border-brd-subtle bg-surface-raised shadow-high z-50 py-1 overflow-hidden"
        >
          {THEMES.map(t => {
            const active = t.id === theme
            return (
              <button
                key={t.id}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => {
                  setTheme(t.id)
                  setOpen(false)
                }}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors duration-150 ${
                  active
                    ? 'text-content-primary bg-accent-muted'
                    : 'text-content-secondary hover:text-content-primary hover:bg-accent-muted'
                }`}
              >
                <ThemeSwatch themeId={t.id} size={12} />
                <span className="flex-1">{t.label}</span>
                {active && (
                  <span
                    aria-hidden="true"
                    className="text-xs text-content-tertiary"
                  >
                    ✓
                  </span>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

/**
 * Small filled circle painted with a theme's own accent color. We
 * nest it inside a `.theme-<id>` scope so the same `--accent`
 * variable resolves to that theme's color — useful for rendering
 * destination-theme swatches while another theme is active.
 */
function ThemeSwatch({ themeId, size }: { themeId: ThemeId; size: number }) {
  return (
    <span className={`theme-${themeId} inline-block`} aria-hidden="true">
      <span
        className="block rounded-full"
        style={{
          width: size,
          height: size,
          background: 'var(--accent)',
          boxShadow: '0 0 0 1px var(--border-emphasis)',
        }}
      />
    </span>
  )
}
